import { groupOfCategory, type PartGroup, type PartItem } from './partsRun';
import type { CategoryKind } from './catalogCategories';

// How the supplied-parts list is written INTO a prompt, and how one part is named in a
// summary line. Two phases ask about these same pieces now — Phase 3 shares them out
// among the steps, Phase 4 breaks each step into gestures that handle them — and they
// have to be looking at the same catalogue, under the same ids, or an id in one phase's
// answer means a different part in the other's.

// A part's catalogue group: a tool always falls under "Tools" (its tool flag is the
// authoritative signal, and the prompts read that heading to know what NOT to count as a
// part), everything else under its ontology category, or "Other" when unset/OTHER. The
// prompts are English, so the raw names are used verbatim.
function partCategory(p: PartItem): string {
  if (p.tool) return 'Tools';
  const c = (p.category || '').trim();
  return !c || c.toUpperCase() === 'OTHER' ? 'Other' : c;
}

// WHAT A PART IS, in one of the ontology's three words. The Phase 4 prompt writes it beside
// every part it lists, because the four kinds of sub-step are defined by how many of each a
// gesture takes — "1 component part, 1 hardware part, 0 or 1 tool" is not a rule anyone can
// apply in front of a list that gives only names and letters.
//
// The tool flag wins, exactly as it does in partCategory above: a part flagged a tool is a
// tool whatever folder it was filed in. Everything else resolves through the ontology, so a
// category split off Hardware tomorrow is hardware here with nothing being told.
const KIND_OF_GROUP: Record<PartGroup, CategoryKind> = {
  Components: 'component',
  Hardware: 'hardware',
  Tools: 'tool',
};
export const partKindWord = (p: PartItem): CategoryKind =>
  (p.tool ? 'tool' : KIND_OF_GROUP[groupOfCategory(p.category)]);

// A part's database (catalog) name — the canonical name it keeps across every manual —
// with the printed name (then the other language) as a fallback.
function partName(p: PartItem): string {
  return p.nameEn || p.name || p.nameFr || '';
}

// The label for a part in a summary line: its name with its letter after it, "Backrest frame
// (A)" — falling back to the ref, then to the id, for a part with neither.
//
// It gave the LETTER ALONE, which is how the step prompt's lists came out reading "0: A x1,
// 2: C x2": six ids and six letters, and nothing a model could match against a diagram or
// against a sentence it is about to write. The letter is what the sub-steps reference so it
// stays, after the name, exactly as withLetter puts it everywhere else in this phase.
export function partSummaryLabel(p: PartItem | undefined, id: string): string {
  if (!p) return id;
  const name = partName(p);
  // A part with no name of any kind keeps the old behaviour rather than being introduced as
  // " (A)" — a bare letter says little, and a parenthesis with nothing in front of it says
  // less.
  return name ? withLetter(name, p.letter) : (p.letter || p.ref || id);
}

// NAME FIRST, LETTER AFTER IT — "Rondelle ressort (F)". How this phase names a part wherever
// it names one, and now that includes the prompt: the sub-step sentences reference pieces by
// their letter, so a label carrying both is what lets a model match "the Allen key (I)" in the
// prose to an id in a list. The bare letter alone — which is what the summary label used to
// give the prompt — put "8: I" in front of a model and asked it to pick.
export const withLetter = (name: string, letter: string | null | undefined) =>
  (letter ? `${name} (${letter})` : name);

// A PART'S NAME IN THE LANGUAGE ON SCREEN, and the other one only if that one is empty.
//
// The distinction matters because a part has three names — the one printed in the manual, and
// one authored in each language — and half this app reached for the French first whatever the
// toggle said. That is how "Structure dossier et pieds arrière (A)" turned up in a selector
// labelled PART PLACED with the app in English, beside a sentence reading "Lay the backrest
// and rear legs frame (A) flat". Same part, two names, one page.
//
// The printed name sits between the two rather than last: it is what the manual itself calls
// the piece, so it is a better answer than the same piece named in the wrong language.
// A part the list no longer has (re-listed since the steps were read) has no name: ''.
export const namedIn = (
  p: { name?: string | null; nameEn?: string | null; nameFr?: string | null } | undefined,
  language: string,
): string => (!p ? '' : language === 'fr'
  ? p.nameFr || p.name || p.nameEn || ''
  : p.nameEn || p.name || p.nameFr || '');

// THE ID A PERSON AND A MODEL SEE, which is one more than the id this app stores.
//
// A part's identity is its position in Phase 2's list, and a list is an array: the first part
// is 0. That is right for the code and wrong everywhere it is read — "0 - Backrest frame (A)"
// reads as a part with no number, and a model given a list starting at 0 answers about the
// second row when it means the first often enough to matter.
//
// So the offset lives at the two edges and nowhere between: every prompt and every label shows
// `shownId`, every answer that comes back is put through `storedId` at the moment it lands, and
// everything in between — the arrays, the stored sub-steps, the reconciliation, ten thousand
// answers already on disk — goes on counting from zero, unchanged and uninterpreted.
// TWO DIGITS, ZERO-LED: "01", "07", "12". A supplied part and an assembled piece are both
// numbers on a chip, and the only thing telling them apart was how big the number was — "7"
// against "301", which a reader has to know the ranges to read. A leading zero says "this one
// came in the box" at a glance, and it says it in the prompts as well as on the chips: the
// ids in a prompt are the ids in the answers and the ids on screen, and three vocabularies
// for one number is what this app spends its comments apologising for.
//
// Numbers parse the same either way — Number('07') is 7 — so storedId and every parser
// downstream are untouched. Anything past 99 is already two digits and unchanged.
export const shownId = (id: string | number) => String(Number(id) + 1).padStart(2, '0');

// And back. `limit` is how many parts the manual has: an id past it is not a part of this
// list at all but a piece an earlier sub-step assembled (those are numbered from 101 — see
// madePieces), and shifting one of those would name a piece that does not exist.
export const storedId = (id: string, limit: number) => {
  const n = Number(id);
  return Number.isFinite(n) && n >= 1 && n <= limit ? String(n - 1) : id;
};

// One catalogue line: "- <id>: <name> <size> (letter X, ref Y, supplied xN)".
function catalogueLine(p: PartItem, id: number): string {
  let desc = [partName(p), p.size?.spec || ''].filter(Boolean).join(' ');
  const tags: string[] = [];
  if (p.letter) tags.push(`letter ${p.letter}`);
  if (p.ref) tags.push(`ref ${p.ref}`);
  if (p.qty != null) tags.push(`supplied x${p.qty}`);
  if (tags.length) desc = (desc ? `${desc} ` : '') + `(${tags.join(', ')})`;
  return `- ${shownId(id)}: ${desc || '(no description)'}`;
}

// THE SAME LINES FOR A CHOSEN FEW, ungrouped. Phase 3 shares the parts out and needs the
// whole list; Phase 4 works on one step and needs ids for exactly two things the step's own
// adds block does not carry — the tools, and the pieces already assembled. Written from the
// same catalogueLine, so an id means the same part with the same letter in both prompts.
export function catalogueSubset(items: PartItem[], ids: number[]): string {
  return ids.filter((i) => items[i]).map((i) => catalogueLine(items[i], i)).join('\n');
}

// The full supplied-parts catalogue, grouped by category (first-seen order), each group
// titled by its category name. The id is the part's index in the Phase 2 list — the id
// every phase answers in.
export function catalogueText(items: PartItem[]): string {
  const groups = new Map<string, string[]>();
  items.forEach((p, i) => {
    const cat = partCategory(p);
    const arr = groups.get(cat) ?? [];
    arr.push(catalogueLine(p, i));
    groups.set(cat, arr);
  });
  return [...groups.entries()].map(([cat, lines]) => `${cat}:\n${lines.join('\n')}`).join('\n\n');
}
